// src/components/features/content/form-steps/ReferencesStep.jsx
import React from 'react';
import {
  Grid,
  Typography,
  Box,
  useTheme,
  alpha,
} from '@mui/material';
import ContentReferences from '../ContentReferences';

/**
 * ReferencesStep component for the content form
 * Allows managing references in the content creation/edit workflow
 */
const ReferencesStep = ({ 
  formik, 
  tempReferences = [],  // References added but not yet uploaded
  setTempReferences,
  isEditMode = false
}) => {
  const theme = useTheme();
  
  // Get references from formik values or use empty array
  const references = formik.values.references || [];
  
  // Handle reference change (add, edit, remove)
  const handleReferencesChange = (updatedReferences) => {
    formik.setFieldValue('references', updatedReferences);
  };
  
  // Handle temp reference change (for newly added references that need to be uploaded)
  const handleTempReferencesChange = (updatedReferences) => {
    setTempReferences(updatedReferences);
  };
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          References & Inspiration
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Add references, inspiration, or source material that helped create this content.
          You can upload images, videos, documents, or add external links.
        </Typography>
      </Grid>
      
      {/* Content References */}
      <Grid item xs={12}>
        <Box sx={{ 
          mb: 3, 
          p: 3, 
          bgcolor: alpha(theme.palette.background.default, 0.5),
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${theme.palette.divider}`
        }}>
          {/* Existing references from formik values */}
          <ContentReferences 
            references={references}
            onRefChange={handleReferencesChange}
            canEdit={true}
          />
        </Box>
      </Grid>
      
      {/* Temporary References */}
      {tempReferences.length > 0 && (
        <Grid item xs={12}>
          <Box sx={{ 
            mb: 3, 
            p: 3, 
            bgcolor: alpha(theme.palette.info.main, 0.05),
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
          }}>
            <Typography variant="subtitle1" gutterBottom color="info.main">
              New References (Not Yet Uploaded)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              These references will be uploaded when you save the content.
            </Typography>
            
            <ContentReferences 
              references={tempReferences}
              onRefChange={handleTempReferencesChange}
              canEdit={true}
            />
          </Box>
        </Grid>
      )}
      
      {/* Reference Tags */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Why References Matter
        </Typography>
        <Typography variant="body2" paragraph>
          Adding references helps document the inspiration and source material that went into
          creating this content. This is valuable for:
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box sx={{ 
            p: 2, 
            bgcolor: alpha(theme.palette.success.main, 0.05),
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            flex: '1 1 200px'
          }}>
            <Typography variant="subtitle2" color="success.main" gutterBottom>
              Content Transparency
            </Typography>
            <Typography variant="body2">
              Document your sources and inspiration to maintain transparency in your creative process.
            </Typography>
          </Box>
          
          <Box sx={{ 
            p: 2, 
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            flex: '1 1 200px'
          }}>
            <Typography variant="subtitle2" color="primary.main" gutterBottom>
              Team Collaboration
            </Typography>
            <Typography variant="body2">
              Help your team understand what resources influenced the final content.
            </Typography>
          </Box>
          
          <Box sx={{ 
            p: 2, 
            bgcolor: alpha(theme.palette.secondary.main, 0.05),
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            flex: '1 1 200px'
          }}>
            <Typography variant="subtitle2" color="secondary.main" gutterBottom>
              Content Consistency
            </Typography>
            <Typography variant="body2">
              Maintain visual and tonal consistency by referencing previous successful content.
            </Typography>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ReferencesStep;